// src/render/map/canopy-fade.ts — pure fade-group compute for canopy + roof-overhang layers.
//
// buildFadeGroups: 4-connected BFS flood-fill over non-empty tiles; each connected
// component becomes one FadeGroup with tiles[], world-px bounding box, tweenCounter, and
// initial state 'visible'. No treeId/buildingId/tile.properties reads — grouping is purely
// positional (Open Question 1 resolution: per-cell custom properties are infeasible on a
// Tiled tilelayer; see 08-RESEARCH.md Pitfall 2).
//
// nextFadeState: pure state-machine decision (enter → fade-in, exit → fade-out) with
// Re-trigger Guard. GameScene (Plan 04) owns the Phaser tween side; this module is tween-free.
//
// Phaser imported for type annotations only (TilemapLayer, Tile). Module has no runtime
// Phaser dependency and is 100% unit-testable without a Phaser context.
//
// Sim/render boundary: module is render-only. Never imported by sim/* files.
// Source: 08-RESEARCH.md Pattern 2 (BFS body), 08-UI-SPEC.md §FadeGroup Data Shape.

import type * as Phaser from 'phaser';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface FadeGroup {
  /** Sequential label assigned at buildFadeGroups() time, e.g. "group-1", "group-2". */
  id: string;
  /** All tiles belonging to this connected component. */
  tiles: Phaser.Tilemaps.Tile[];
  /** World-pixel bounding box: left=minCol*tileW … bottom=(maxRow+1)*tileH (D-10). */
  box: { left: number; top: number; right: number; bottom: number };
  /**
   * Mutable tween target. Phaser.Tilemaps.Tile is not a TweenTarget; tweening a plain
   * counter and applying the value in onUpdate is the project-locked pattern (STATE.md).
   */
  tweenCounter: { alpha: number };
  state: 'visible' | 'fading-in' | 'transparent' | 'fading-out';
}

export type FadeAction = 'start-in' | 'start-out' | 'none';

export interface FadeDecision {
  action: FadeAction;
  state: FadeGroup['state'];
}

// ─── buildFadeGroups ─────────────────────────────────────────────────────────

/**
 * buildFadeGroups — connected-component flood-fill grouper.
 *
 * Scans every non-empty tile in `layer` (index > 0) and groups 4-connected tiles
 * into independent FadeGroups. The seen-set ensures O(tiles) cost — each tile is
 * visited at most once. Safe for the 160×144 (23,040-cell) production Mouchao map.
 *
 * @param layer  CPU TilemapLayer (gpu:false); must expose forEachTile + getTileAt.
 * @param tileW  Tile width in px (32 for Mouchao).
 * @param tileH  Tile height in px (32 for Mouchao).
 */
export function buildFadeGroups(
  layer: Phaser.Tilemaps.TilemapLayer,
  tileW: number,
  tileH: number,
): FadeGroup[] {
  const groups: FadeGroup[] = [];
  const seen = new Set<string>();
  let groupIndex = 0;

  layer.forEachTile((startTile) => {
    // Skip empty tiles (Phaser empty sentinel is -1; authoring may use 0).
    if (startTile.index <= 0) return;
    const startKey = `${startTile.x.toString()},${startTile.y.toString()}`;
    if (seen.has(startKey)) return;

    // BFS (implemented as DFS via pop() — traversal order does not affect grouping).
    const tiles: Phaser.Tilemaps.Tile[] = [];
    const queue: Array<[number, number]> = [[startTile.x, startTile.y]];
    let minX = startTile.x;
    let maxX = startTile.x;
    let minY = startTile.y;
    let maxY = startTile.y;

    while (queue.length > 0) {
      const entry = queue.pop();
      if (!entry) break;
      const [x, y] = entry;
      const key = `${x.toString()},${y.toString()}`;
      if (seen.has(key)) continue;
      const tile = layer.getTileAt(x, y);
      if (!tile || tile.index <= 0) continue;
      seen.add(key);
      tiles.push(tile);
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
      // Push 4-connected neighbours (bounds-check deferred to getTileAt returning null).
      queue.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]);
    }

    if (tiles.length === 0) return;

    groupIndex += 1;
    groups.push({
      id: `group-${groupIndex.toString()}`,
      tiles,
      box: {
        left: minX * tileW,
        top: minY * tileH,
        right: (maxX + 1) * tileW,
        bottom: (maxY + 1) * tileH,
      },
      tweenCounter: { alpha: 1 },
      state: 'visible',
    });
  });

  return groups;
}

// ─── nextFadeState ────────────────────────────────────────────────────────────

/**
 * nextFadeState — pure Re-trigger Guard decision.
 *
 * Returns the tween action to take and the resulting group state. The caller
 * (GameScene.update) is responsible for calling tweens.killTweensOf(group.tweenCounter)
 * when action is 'start-in' or 'start-out' before starting the new tween.
 *
 * Truth table (08-UI-SPEC §Re-trigger Guard):
 *   visible    + inside  → start-in  / fading-in
 *   fading-out + inside  → start-in  / fading-in   (conflict kill+restart)
 *   fading-in  + inside  → none      / fading-in   (already heading in)
 *   transparent+ inside  → none      / transparent  (already arrived)
 *   transparent+ outside → start-out / fading-out
 *   fading-in  + outside → start-out / fading-out  (conflict kill+restart)
 *   fading-out + outside → none      / fading-out  (already heading out)
 *   visible    + outside → none      / visible     (already arrived)
 */
export function nextFadeState(
  state: FadeGroup['state'],
  inside: boolean,
): FadeDecision {
  if (inside) {
    if (state === 'visible' || state === 'fading-out') {
      return { action: 'start-in', state: 'fading-in' };
    }
    // 'fading-in' | 'transparent' — already heading in or fully faded
    return { action: 'none', state };
  } else {
    if (state === 'transparent' || state === 'fading-in') {
      return { action: 'start-out', state: 'fading-out' };
    }
    // 'fading-out' | 'visible' — already heading out or fully opaque
    return { action: 'none', state };
  }
}
